import { format, startOfMonth, subMonths } from 'date-fns'
import { routes } from '@/routes'
import type { TKey } from '@/translations/i18n'
import type { UserRoles } from '../auth/types'
import type {
	CsvUploadStatus,
	EssentialAmenityType,
	FinalReviewStatus,
	GalleryImage,
	MMDataOverviewDatePreset,
	MMParkingLotStatusFilter,
	MMSubmissionInventory,
	MMSubmissionStatus,
	PropertyType,
	QuoteGenerationBlocker,
	QuoteGenerationState,
	SubmissionActivityEntry,
	SubmissionSource,
	SubmissionStatus,
	SubmissionType
} from './types'

export const FULL_ACCESS_USER_ROLES: UserRoles[] = [
	'admin',
	'luxe_admin'
] as const

export const OPS_ACCESS_USER_ROLES: UserRoles[] = [
	...FULL_ACCESS_USER_ROLES,
	'luxe_ops'
] as const

export const getLuxeRedirectPath = (
	role: UserRoles | undefined
): string | null => {
	switch (role) {
		case 'luxe_vetter':
			return routes.LUXE_SUBMISSIONS_QUEUE_VISUAL_VETTING.handle.fullPath
		case 'luxe_admin':
		case 'luxe_ops':
			return routes.LUXE_SUBMISSIONS_INITIAL_EVALUATIONS.handle.fullPath
		case 'marketing_manager':
			return routes.LUXE_SUBMISSIONS_MM_SUBMISSIONS.handle.fullPath
		default:
			return null
	}
}

export const sourceToBadgeStyle = {
	marketing_manager: 'bg-cyan-50 border-cyan-200 text-cyan-700',
	elevate_airbnb_host: 'bg-primary-50 border-primary-200 text-primary-700',
	elevate_external_host: 'bg-primary-50 border-primary-200 text-primary-700',
	luxe_ops_bulk_import: 'bg-neutral-50 border-neutral-200 text-neutral-700',
	salesforce_sync: 'bg-blue-50 border-blue-200 text-blue-700'
} as const satisfies Record<SubmissionSource, string>

export const sourceToLabelTKey = {
	elevate_airbnb_host: 'luxe_submissions:source_elevate_airbnb_host',
	elevate_external_host: 'luxe_submissions:source_elevate_external_host',
	marketing_manager: 'luxe_submissions:source_marketing_manager',
	luxe_ops_bulk_import: 'luxe_submissions:source_bulk_import',
	salesforce_sync: 'luxe_submissions:source_salesforce_sync'
} as const satisfies Record<SubmissionSource, TKey>

export const sourceOptions = [
	{
		value: 'elevate_airbnb_host',
		label: sourceToLabelTKey.elevate_airbnb_host
	},
	{
		value: 'elevate_external_host',
		label: sourceToLabelTKey.elevate_external_host
	},
	{
		value: 'marketing_manager',
		label: sourceToLabelTKey.marketing_manager
	},
	{
		value: 'luxe_ops_bulk_import',
		label: sourceToLabelTKey.luxe_ops_bulk_import
	},
	{
		value: 'salesforce_sync',
		label: sourceToLabelTKey.salesforce_sync
	}
] as const satisfies readonly { value: SubmissionSource; label: TKey }[]

/** Pre-check only ever receives submissions from these sources. */
export const precheckSourceOptions = [
	{
		value: 'elevate_airbnb_host',
		label: sourceToLabelTKey.elevate_airbnb_host
	},
	{
		value: 'elevate_external_host',
		label: sourceToLabelTKey.elevate_external_host
	},
	{
		value: 'salesforce_sync',
		label: sourceToLabelTKey.salesforce_sync
	}
] as const satisfies readonly { value: SubmissionSource; label: TKey }[]

export const submissionTypeToLabelTKey = {
	marketplace_conversion:
		'luxe_submissions:submission_type_marketplace_conversion',
	field_team_nal: 'luxe_submissions:submission_type_field_team'
} as const satisfies Record<SubmissionType, TKey>

export const submissionTypeOptions = [
	{
		value: 'marketplace_conversion',
		label: submissionTypeToLabelTKey.marketplace_conversion
	},
	{
		value: 'field_team_nal',
		label: submissionTypeToLabelTKey.field_team_nal
	}
] as const satisfies readonly { value: SubmissionType; label: TKey }[]

export const submissionStatusToBadgeStyle = {
	pending: 'bg-neutral-100 border-neutral-300 text-neutral-700',
	rejected: 'bg-danger-50 border-danger-200 text-danger-700',
	completed: 'bg-success-50 border-success-200 text-success-700',
	in_progress: 'bg-warning-50 border-warning-200 text-warning-700',
	locked: 'bg-neutral-100 border-neutral-300 text-neutral-700',
	skipped: 'bg-neutral-100 border-neutral-300 text-neutral-700'
} as const satisfies Record<SubmissionStatus, string>

export const submissionStatusToLabelTKey = {
	pending: 'luxe_submissions:status_pending',
	in_progress: 'luxe_submissions:status_in_progress',
	completed: 'luxe_submissions:status_completed',
	rejected: 'luxe_submissions:status_rejected',
	locked: 'luxe_submissions:status_locked',
	skipped: 'luxe_submissions:status_skipped'
} as const satisfies Record<SubmissionStatus, TKey>

/**
 * The ML Review population is a pre-check state rather than a submission
 * status, but it is surfaced in the queue's status column in its place, so it
 * carries its own badge style and label.
 */
export const ML_REVIEW_BADGE_STYLE =
	'bg-neutral-100 border-neutral-300 text-neutral-700' as const

export const statusOptions = [
	{ value: 'pending', label: submissionStatusToLabelTKey.pending },
	{ value: 'in_progress', label: submissionStatusToLabelTKey.in_progress },
	{ value: 'completed', label: submissionStatusToLabelTKey.completed }
] as const satisfies readonly { value: SubmissionStatus; label: TKey }[]

// Final Review status badge styles (different statuses than Visual Vetting)
export const finalReviewStatusToBadgeStyle = {
	needs_review: 'bg-neutral-100 border-neutral-300 text-neutral-700',
	needs_review_mms: 'bg-neutral-100 border-neutral-300 text-neutral-700',
	failed: 'bg-danger-50 border-danger-200 text-danger-700',
	passed: 'bg-success-50 border-success-200 text-success-700'
} as const satisfies Record<FinalReviewStatus, string>

export const finalReviewStatusToLabelTKey = {
	needs_review: 'luxe_submissions:status_needs_review',
	needs_review_mms: 'luxe_submissions:status_needs_review_mms',
	failed: 'luxe_submissions:status_failed',
	passed: 'luxe_submissions:status_passed'
} as const satisfies Record<FinalReviewStatus, TKey>

export const finalReviewStatusOptions = [
	{ value: 'needs_review', label: finalReviewStatusToLabelTKey.needs_review },
	{
		value: 'needs_review_mms',
		label: finalReviewStatusToLabelTKey.needs_review_mms
	},
	{ value: 'failed', label: finalReviewStatusToLabelTKey.failed },
	{ value: 'passed', label: finalReviewStatusToLabelTKey.passed }
] as const satisfies readonly { value: FinalReviewStatus; label: TKey }[]

const SUCCESS_BADGE_STYLE = 'bg-success-50 border-success-200 text-success-700'
const DANGER_BADGE_STYLE = 'bg-danger-50 border-danger-200 text-danger-700'

// VV Score badge style helper
// Passed — Meets Standard (≥75) green
// Needs Review — Near Miss (60–74) orange
// Fail — (< 60) red
// With luxe-vv2-final-score-display on the Final Score is a plain pass/fail
// against LUXE_PASS_MARK, so there is no near-miss band: green or red only.
export const getVvScoreBadgeStyle = (
	score: number,
	isFinalScoreEnabled = false
): string => {
	if (isFinalScoreEnabled) {
		return score >= LUXE_PASS_MARK
			? SUCCESS_BADGE_STYLE
			: DANGER_BADGE_STYLE
	}
	if (score >= 75) {
		return SUCCESS_BADGE_STYLE
	}
	if (score >= 60) {
		return 'bg-orange-50 border-orange-200 text-orange-700'
	}
	return DANGER_BADGE_STYLE
}

/**
 * The number to put on screen for a submission. With
 * luxe-vv2-final-score-display on the backend fills `finalScore` — the design
 * score cut by 30% per failed blocking criterion — and the queues sort by it,
 * so the Final Score is what every score badge must show. With the flag off it
 * arrives null and the design score stays on screen, exactly as today.
 */
export const resolveDisplayScore = ({
	designScore,
	finalScore,
	isFinalScoreEnabled
}: {
	designScore: number | null
	finalScore?: number | null
	isFinalScoreEnabled: boolean
}): number | null => {
	const score =
		isFinalScoreEnabled && finalScore != null ? finalScore : designScore

	return score == null ? null : Math.floor(score)
}

/**
 * The score a listing has to reach to be eligible for Luxe. It is the Final
 * Score that is measured against it, not the design score: a design score of 90
 * with one failed blocker lands at 63 and misses the mark.
 */
export const LUXE_PASS_MARK = 66

/**
 * The text colour of a score shown on its own, outside a badge: green at the
 * pass mark and above, red below it or with no score at all.
 */
export const getScoreTextColor = (score: number | null): string =>
	score != null && score >= LUXE_PASS_MARK
		? 'text-success-600'
		: 'text-danger-600'

/** The blocking criteria a listing is scored against: B1-B3 and TS1-TS3. */
export const BLOCKING_CRITERIA_COUNT = 6

/** The verdict the Final Score card puts next to the score. */
export type FinalScoreEligibility = 'eligible' | 'not_eligible'

/**
 * The verdict is read off the Final Score alone. Failed blockers and table
 * stakes need no separate say here because they are already priced into that
 * number: each one multiplies the design score by 0.7.
 */
export const resolveFinalScoreEligibility = (
	score: number
): FinalScoreEligibility =>
	score >= LUXE_PASS_MARK ? 'eligible' : 'not_eligible'

/**
 * The Salesforce-driven state a Quote Generation row should be rendered as.
 * The backend already answers "ready" for a state whose flag is off, but each
 * state is checked against its own flag here too, so one flag being off can
 * never let the other state through.
 */
export const resolveQuoteGenerationState = ({
	state,
	isBlockedStateEnabled,
	isGeneratedStateEnabled
}: {
	state?: QuoteGenerationState | null
	isBlockedStateEnabled: boolean
	isGeneratedStateEnabled: boolean
}): QuoteGenerationState => {
	if (state === 'blocked') {
		return isBlockedStateEnabled ? 'blocked' : 'ready'
	}
	if (state === 'quote_generated') {
		return isGeneratedStateEnabled ? 'quote_generated' : 'ready'
	}
	// null means no Salesforce signal was ever recorded — render it like ready
	return 'ready'
}

export const essentialAmenityToLabelTKey = {
	air_conditioning: 'luxe_submissions:essential_amenity.air_conditioning',
	bathroom_capacity: 'luxe_submissions:essential_amenity.bathroom_capacity',
	dining_seating: 'luxe_submissions:essential_amenity.dining_seating',
	heating: 'luxe_submissions:essential_amenity.heating',
	kitchen_appliances: 'luxe_submissions:essential_amenity.kitchen_appliances',
	primary_bedroom_ensuite:
		'luxe_submissions:essential_amenity.primary_bedroom_ensuite',
	washer_dryer: 'luxe_submissions:essential_amenity.washer_dryer'
} as const satisfies Record<EssentialAmenityType, TKey>

// Property Type badge styles
export const propertyTypeToBadgeStyle: Partial<Record<PropertyType, string>> = {
	apartment: 'bg-blue-50 border-blue-200 text-blue-700',
	house: 'bg-pink-50 border-pink-200 text-pink-700',
	condominium: 'bg-purple-50 border-purple-200 text-purple-700',
	villa: 'bg-warning-50 border-warning-200 text-warning-700',
	townhouse: 'bg-success-50 border-success-200 text-success-700',
	cabin: 'bg-neutral-100 border-neutral-300 text-neutral-700'
}

export const propertyTypeToLabelTKey = {
	aparthotel: 'luxe_submissions:property_type_aparthotel',
	apartment: 'luxe_submissions:property_type_apartment',
	barn: 'luxe_submissions:property_type_barn',
	bnb: 'luxe_submissions:property_type_bnb',
	boat: 'luxe_submissions:property_type_boat',
	boutique_hotel: 'luxe_submissions:property_type_boutique_hotel',
	bungalow: 'luxe_submissions:property_type_bungalow',
	cabin: 'luxe_submissions:property_type_cabin',
	campsite: 'luxe_submissions:property_type_campsite',
	casa_particular: 'luxe_submissions:property_type_casa_particular',
	castle: 'luxe_submissions:property_type_castle',
	cave: 'luxe_submissions:property_type_cave',
	chalet: 'luxe_submissions:property_type_chalet',
	condominium: 'luxe_submissions:property_type_condominium',
	cottage: 'luxe_submissions:property_type_cottage',
	cycladic_house: 'luxe_submissions:property_type_cycladic_house',
	dammuso: 'luxe_submissions:property_type_dammuso',
	dome_house: 'luxe_submissions:property_type_dome_house',
	earthhouse: 'luxe_submissions:property_type_earthhouse',
	farm_stay: 'luxe_submissions:property_type_farm_stay',
	guest_suite: 'luxe_submissions:property_type_guest_suite',
	guesthouse: 'luxe_submissions:property_type_guesthouse',
	heritage_hotel: 'luxe_submissions:property_type_heritage_hotel',
	holiday_park: 'luxe_submissions:property_type_holiday_park',
	hostel: 'luxe_submissions:property_type_hostel',
	hotel: 'luxe_submissions:property_type_hotel',
	house: 'luxe_submissions:property_type_house',
	houseboat: 'luxe_submissions:property_type_houseboat',
	hut: 'luxe_submissions:property_type_hut',
	igloo: 'luxe_submissions:property_type_igloo',
	island: 'luxe_submissions:property_type_island',
	kezhan: 'luxe_submissions:property_type_kezhan',
	lighthouse: 'luxe_submissions:property_type_lighthouse',
	lodge: 'luxe_submissions:property_type_lodge',
	loft: 'luxe_submissions:property_type_loft',
	minsu: 'luxe_submissions:property_type_minsu',
	pension: 'luxe_submissions:property_type_pension',
	plane: 'luxe_submissions:property_type_plane',
	ranch: 'luxe_submissions:property_type_ranch',
	religious_building: 'luxe_submissions:property_type_religious_building',
	resort: 'luxe_submissions:property_type_resort',
	riad: 'luxe_submissions:property_type_riad',
	rv: 'luxe_submissions:property_type_rv',
	ryokan: 'luxe_submissions:property_type_ryokan',
	serviced_apartment: 'luxe_submissions:property_type_serviced_apartment',
	shepherds_hut: 'luxe_submissions:property_type_shepherds_hut',
	shipping_container: 'luxe_submissions:property_type_shipping_container',
	tent: 'luxe_submissions:property_type_tent',
	tiny_house: 'luxe_submissions:property_type_tiny_house',
	tipi: 'luxe_submissions:property_type_tipi',
	tower: 'luxe_submissions:property_type_tower',
	townhouse: 'luxe_submissions:property_type_townhouse',
	train: 'luxe_submissions:property_type_train',
	treehouse: 'luxe_submissions:property_type_treehouse',
	trullo: 'luxe_submissions:property_type_trullo',
	villa: 'luxe_submissions:property_type_villa',
	windmill: 'luxe_submissions:property_type_windmill',
	yurt: 'luxe_submissions:property_type_yurt'
} as const satisfies Record<PropertyType, TKey>
export const RESIDENCE_TYPE_OPTIONS_LABELS = {
	single_family_beach_coastal:
		'luxe_submissions:score_form.residence_type_options.single_family_beach',
	single_family_metropolitan_urban_historic:
		'luxe_submissions:score_form.residence_type_options.single_family_metro',
	single_family_lifestyle_rural:
		'luxe_submissions:score_form.residence_type_options.single_family_lifestyle',
	single_family_mountains:
		'luxe_submissions:score_form.residence_type_options.single_family_mountains',
	apartment_condo_townhome_any_geo:
		'luxe_submissions:score_form.residence_type_options.apartment_condo_townhome'
} as const satisfies Record<string, TKey>

/** Translation keys for each question code (lowercase).*/
export const QUESTION_TRANSLATIONS: Record<
	string,
	{ title: TKey; description?: TKey; shortLabel?: TKey; longLabel?: TKey }
> = {
	b1: {
		title: 'luxe_submissions:score_form.b1_title',
		description: 'luxe_submissions:score_form.b1_description',
		shortLabel: 'luxe_submissions:score_form.b1_short_label',
		longLabel: 'luxe_submissions:score_form.b1_long_label'
	},
	b2: {
		title: 'luxe_submissions:score_form.b2_title',
		description: 'luxe_submissions:score_form.b2_description',
		shortLabel: 'luxe_submissions:score_form.b2_short_label',
		longLabel: 'luxe_submissions:score_form.b2_long_label'
	},
	b3: {
		title: 'luxe_submissions:score_form.b3_title',
		shortLabel: 'luxe_submissions:score_form.b3_short_label',
		longLabel: 'luxe_submissions:score_form.b3_long_label'
	},
	ts1: {
		title: 'luxe_submissions:score_form.ts1_long_label',
		description: 'luxe_submissions:score_form.ts1_description',
		shortLabel: 'luxe_submissions:score_form.ts1_short_label',
		longLabel: 'luxe_submissions:score_form.ts1_long_label'
	},
	ts2: {
		title: 'luxe_submissions:score_form.ts2_long_label',
		description: 'luxe_submissions:score_form.ts2_description',
		shortLabel: 'luxe_submissions:score_form.ts2_short_label',
		longLabel: 'luxe_submissions:score_form.ts2_long_label'
	},
	ts3: {
		title: 'luxe_submissions:score_form.ts3_long_label',
		description: 'luxe_submissions:score_form.ts3_description',
		shortLabel: 'luxe_submissions:score_form.ts3_short_label',
		longLabel: 'luxe_submissions:score_form.ts3_long_label'
	},
	ts4: {
		title: 'luxe_submissions:score_form.ts4_long_label',
		description: 'luxe_submissions:score_form.ts4_description',
		shortLabel: 'luxe_submissions:score_form.ts4_short_label',
		longLabel: 'luxe_submissions:score_form.ts4_long_label'
	},
	ts5: {
		title: 'luxe_submissions:score_form.ts5_long_label',
		description: 'luxe_submissions:score_form.ts5_description',
		shortLabel: 'luxe_submissions:score_form.ts5_short_label',
		longLabel: 'luxe_submissions:score_form.ts5_long_label'
	},
	ts6: {
		title: 'luxe_submissions:score_form.ts6_long_label',
		description: 'luxe_submissions:score_form.ts6_description',
		shortLabel: 'luxe_submissions:score_form.ts6_short_label',
		longLabel: 'luxe_submissions:score_form.ts6_long_label'
	},
	ts7: {
		title: 'luxe_submissions:score_form.ts7_long_label',
		description: 'luxe_submissions:score_form.ts7_description',
		shortLabel: 'luxe_submissions:score_form.ts7_short_label',
		longLabel: 'luxe_submissions:score_form.ts7_long_label'
	},
	q1: {
		title: 'luxe_submissions:score_form.q1_title',
		description: 'luxe_submissions:score_form.q1_description'
	},
	q2: {
		title: 'luxe_submissions:score_form.q2_title',
		description: 'luxe_submissions:score_form.q2_description'
	},
	q3: {
		title: 'luxe_submissions:score_form.q3_title',
		description: 'luxe_submissions:score_form.q3_description'
	},
	q4: {
		title: 'luxe_submissions:score_form.q4_title',
		description: 'luxe_submissions:score_form.q4_description'
	},
	q5: {
		title: 'luxe_submissions:score_form.q5_title',
		description: 'luxe_submissions:score_form.q5_description'
	},
	q6: {
		title: 'luxe_submissions:score_form.q6_title',
		description: 'luxe_submissions:score_form.q6_description'
	},
	q7: {
		title: 'luxe_submissions:score_form.q7_title',
		description: 'luxe_submissions:score_form.q7_description'
	},
	q8: {
		title: 'luxe_submissions:score_form.q8_title',
		description: 'luxe_submissions:score_form.q8_description'
	},
	q9: {
		title: 'luxe_submissions:score_form.q9_title',
		description: 'luxe_submissions:score_form.q9_description'
	},
	q10: {
		title: 'luxe_submissions:score_form.q10_title',
		description: 'luxe_submissions:score_form.q10_description'
	},
	q11: {
		title: 'luxe_submissions:score_form.q11_title',
		description: 'luxe_submissions:score_form.q11_description'
	},
	q12: {
		title: 'luxe_submissions:score_form.q12_title',
		description: 'luxe_submissions:score_form.q12_description'
	},
	q13: {
		title: 'luxe_submissions:score_form.q13_title',
		description: 'luxe_submissions:score_form.q13_description'
	},
	rt: {
		title: 'luxe_submissions:score_form.residence_type'
	},
	tsn: {
		title: 'luxe_submissions:score_form.table_stakes_notes_title',
		description:
			'luxe_submissions:score_form.table_stakes_notes_description'
	}
} as const satisfies Record<
	string,
	{ title: TKey; description?: TKey; shortLabel?: TKey; longLabel?: TKey }
>

/** Extract numeric part from code for sorting: "TS4" → 4, "B1" → 1, "Q13" → 13 */
export function sortQuestionsByCode<T extends { code: string }>(
	questions: T[]
): T[] {
	return [...questions].sort((a, b) => {
		const numA = Number.parseInt(a.code.replace(/\D/g, ''), 10)
		const numB = Number.parseInt(b.code.replace(/\D/g, ''), 10)
		return numA - numB
	})
}

/**
 * Failed criteria as one readable list — "Full Kitchen (TS2) · Living & Dining
 * (TS3)". Takes any question codes, table stakes and blockers alike, since
 * `failedBlockers` reports both in the same form. Codes are deduplicated because
 * one comes back per failed question underneath a criterion, and repeating a
 * criterion in the banner only makes the sentence harder to read.
 */
export const formatFailedCriteria = (
	codes: string[],
	t: (key: TKey) => string
): string =>
	[...new Set(codes.map((code) => code.toLowerCase()))]
		.map((code) => {
			const question = QUESTION_TRANSLATIONS[code]

			if (!question?.longLabel) {
				return code.toUpperCase()
			}

			const shortLabel = question.shortLabel
				? t(question.shortLabel)
				: code.toUpperCase()

			return `${t(question.longLabel)} (${shortLabel})`
		})
		.join(' · ')

export const HARD_STAKE_CODES = new Set(['TS1', 'TS2', 'TS3'])
export const SOFT_STAKE_CODES = new Set(['TS4', 'TS5'])

export const FAIL_VALUES = new Set(['no', 'fail'])

export const UNSURE_VALUES = new Set([
	'unsure',
	'no_has_alternative',
	'no_ensuite',
	'bed_size_unclear',
	'marginal',
	'uncertain',
	'partial',
	'unable_to_verify',
	'unable_to_determine'
])

// MM Submission inventory options and mappings
export const mmInventoryToLabelTKey = {
	standard_mp_conversion: 'luxe_submissions:inventory_standard_mp_conversion',
	standard_nal: 'luxe_submissions:inventory_standard_nal',
	branded_residence_mp_conversion:
		'luxe_submissions:inventory_branded_residence_mp_conversion',
	branded_residence_nal: 'luxe_submissions:inventory_branded_residence_nal'
} as const satisfies Record<MMSubmissionInventory, TKey>

export const mmInventoryOptions = [
	{
		value: 'standard_mp_conversion',
		label: mmInventoryToLabelTKey.standard_mp_conversion
	},
	{ value: 'standard_nal', label: mmInventoryToLabelTKey.standard_nal },
	{
		value: 'branded_residence_mp_conversion',
		label: mmInventoryToLabelTKey.branded_residence_mp_conversion
	},
	{
		value: 'branded_residence_nal',
		label: mmInventoryToLabelTKey.branded_residence_nal
	}
] as const satisfies readonly { value: MMSubmissionInventory; label: TKey }[]

export const mmInventoryToBadgeStyle = {
	standard_mp_conversion: 'bg-cyan-50 border-cyan-200 text-cyan-700',
	standard_nal: 'bg-cyan-50 border-cyan-200 text-cyan-700',
	branded_residence_mp_conversion: 'bg-cyan-50 border-cyan-200 text-cyan-700',
	branded_residence_nal: 'bg-cyan-50 border-cyan-200 text-cyan-700'
} as const satisfies Record<MMSubmissionInventory, string>

// MM Parking Lot status options and mappings
export const mmParkingLotStatusToLabelTKey = {
	pre_onboarding: 'luxe_submissions:status_pre_onboarding',
	failed_standards: 'luxe_submissions:status_failed_standards',
	ready_for_inspection: 'luxe_submissions:status_ready_for_inspection',
	back_in_review: 'luxe_submissions:status_back_in_review'
} as const satisfies Record<MMSubmissionStatus, TKey>

// The statuses a second review can end in
export const mmParkingLotStatusAfterSecondReviewToLabelTKey = {
	failed_standards:
		'luxe_submissions:status_failed_standards_after_second_review',
	pre_onboarding: 'luxe_submissions:status_pre_onboarding_after_second_review'
} as const satisfies Partial<Record<MMSubmissionStatus, TKey>>

export const mmParkingLotStatusOptions = [
	{
		value: 'pre_onboarding',
		label: mmParkingLotStatusToLabelTKey.pre_onboarding
	},
	{
		value: 'pre_onboarding_after_second_review',
		label: mmParkingLotStatusAfterSecondReviewToLabelTKey.pre_onboarding
	},
	{
		value: 'back_in_review',
		label: mmParkingLotStatusToLabelTKey.back_in_review
	},
	{
		value: 'failed_standards',
		label: mmParkingLotStatusToLabelTKey.failed_standards
	},
	{
		value: 'failed_second_review',
		label: mmParkingLotStatusAfterSecondReviewToLabelTKey.failed_standards
	}
] as const satisfies readonly {
	value: MMParkingLotStatusFilter
	label: TKey
}[]

// The request params behind each status filter. Back in review is only ever
// reached through a second review request, so it needs no hadSecondReview.
export const mmParkingLotStatusFilterToParams = {
	pre_onboarding: { status: 'pre_onboarding', hadSecondReview: false },
	pre_onboarding_after_second_review: {
		status: 'pre_onboarding',
		hadSecondReview: true
	},
	back_in_review: { status: 'back_in_review' },
	failed_standards: { status: 'failed_standards', hadSecondReview: false },
	failed_second_review: { status: 'failed_standards', hadSecondReview: true }
} as const satisfies Record<
	MMParkingLotStatusFilter,
	{ status: MMSubmissionStatus; hadSecondReview?: boolean }
>

export const mmParkingLotStatusToBadgeStyle = {
	pre_onboarding: 'bg-neutral-100 border-neutral-300 text-neutral-700',
	failed_standards: 'bg-danger-50 border-danger-200 text-danger-700',
	ready_for_inspection: 'bg-success-50 border-success-200 text-success-700',
	back_in_review: 'bg-warning-50 border-warning-200 text-warning-700'
} as const satisfies Record<MMSubmissionStatus, string>

export const getDateRangeFromPreset = (
	preset: MMDataOverviewDatePreset
): { dateFrom: string; dateTo: string } => {
	const today = new Date()
	const dateTo = format(today, 'yyyy-MM-dd')

	switch (preset) {
		case 'last_6_months':
			return {
				dateFrom: format(subMonths(today, 6), 'yyyy-MM-dd'),
				dateTo
			}
		case 'last_3_months':
			return {
				dateFrom: format(subMonths(today, 3), 'yyyy-MM-dd'),
				dateTo
			}
		default:
			return {
				dateFrom: format(startOfMonth(today), 'yyyy-MM-dd'),
				dateTo
			}
	}
}

export const getReviewerName = ({
	firstName,
	lastName,
	email,
	isMe = false
}: {
	firstName: string | null
	lastName: string | null
	email?: string
	isMe?: boolean
}) => {
	const fullName =
		`${firstName ? firstName : ''} ${lastName ? lastName : ''}`.trim()
	const initials =
		lastName || firstName
			? `${firstName ? firstName[0] : ''}${lastName ? lastName[0] : ''}`.trim()
			: email?.[0].toUpperCase()
	return {
		fullName: `${fullName || email || ''}${isMe ? ' (You)' : ''}`.trim(),
		initials: initials ? initials.toUpperCase() : ''
	}
}

export const toArrayParam = <T>(
	value: T | undefined | null
): T[] | undefined => (value ? [value] : undefined)

export const csvUploadStatusToBadgeStyle = {
	pending_upload: 'bg-neutral-50 border-neutral-200 text-neutral-700',
	uploaded: 'bg-warning-50 border-warning-200 text-warning-700',
	processing: 'bg-warning-50 border-warning-200 text-warning-700',
	completed: 'bg-success-50 border-success-200 text-success-700',
	failed: 'bg-danger-50 border-danger-200 text-danger-700'
} as const satisfies Record<CsvUploadStatus, string>

export const csvUploadStatusToLabelTKey = {
	pending_upload: 'luxe_submissions:csv_upload_status_pending',
	uploaded: 'luxe_submissions:csv_upload_status_processing',
	processing: 'luxe_submissions:csv_upload_status_processing',
	completed: 'luxe_submissions:csv_upload_status_completed',
	failed: 'luxe_submissions:csv_upload_status_failed'
} as const satisfies Record<CsvUploadStatus, TKey>

export const quoteGenerationBlockerToLabelTKey = {
	airbnb_listing_missing:
		'luxe_submissions:quote_generation_blocker.airbnb_listing_missing',
	quote_generated_outside_luminate:
		'luxe_submissions:quote_generated_outside',
	salesforce_related_listing_missing:
		'luxe_submissions:quote_generation_blocker.salesforce_related_listing_missing',
	host_credentials_missing:
		'luxe_submissions:quote_generation_blocker.host_credentials_missing',
	submission_type_missing:
		'luxe_submissions:quote_generation_blocker.submission_type_missing',
	submission_rejected:
		'luxe_submissions:quote_generation_blocker.submission_rejected',
	blocked_by_salesforce:
		'luxe_submissions:quote_generation_blocker.blocked_by_salesforce'
} as const satisfies Record<QuoteGenerationBlocker, TKey>

// Falls back to a generic reason when the backend sends no blocker
export const getQuoteGenerationBlockerLabelTKey = (
	blocker: QuoteGenerationBlocker | null | undefined
): TKey =>
	blocker
		? quoteGenerationBlockerToLabelTKey[blocker]
		: 'luxe_submissions:quote_generation_not_available_description'

export type GalleryPreviewSize = 'large' | 'extraLarge' | 'original'

// Each size steps down to the next smaller large preview before giving up
const GALLERY_PREVIEW_SIZE_FALLBACKS = {
	large: ['large'],
	extraLarge: ['extraLarge', 'large'],
	original: ['original', 'extraLarge', 'large']
} as const satisfies Record<GalleryPreviewSize, GalleryPreviewSize[]>

/**
 * The large previews only exist on Airbnb images, so anything else keeps
 * resolving to the original upload or the extraMedium preview.
 */
export const getGalleryImageUrl = (
	image: GalleryImage,
	size: GalleryPreviewSize
): string | undefined =>
	GALLERY_PREVIEW_SIZE_FALLBACKS[size]
		.map((key) => image.previewUrls?.[key])
		.find(Boolean) ||
	image.imageUrl ||
	image.previewUrls?.extraMedium ||
	undefined

/**
 * The scorecard submission that escalated the listing: the latest visual
 * vetting questionnaire_submitted entry, which carries the escalation note with
 * its author and date. The backend orders the comments oldest first.
 */
export const findEscalationEntry = (comments: SubmissionActivityEntry[]) =>
	comments
		.filter(
			(entry) =>
				entry.stage === 'visual_vetting' &&
				entry.actionType === 'questionnaire_submitted'
		)
		.at(-1)

/**
 * The latest second_review_requested entry: the MM's note asking for another
 * review of the listing. The backend orders the comments oldest first.
 */
export const findSecondReviewEntry = (comments: SubmissionActivityEntry[]) =>
	comments
		.filter((entry) => entry.actionType === 'second_review_requested')
		.at(-1)
